import React from 'react';

import { Box, Container, Link as MuiLink, Typography } from '@mui/material';

import aboutDiscoveryLarge from '../assets/images/about-discovery-1536.webp';
import aboutDiscoverySmall from '../assets/images/about-discovery-768.webp';
import Paragraph from '../components/Paragraph';
import SectionTitle from '../components/SectionTitle';

function AboutUs() {
  return (
    <Container
      sx={{
        paddingTop: '40px',
      }}
    >
      <Container
        sx={{
          paddingTop: '10px',
        }}
      >
        <SectionTitle
          first='About'
          second='us'
          type='titles'
          variant='h6'
          component='h1'
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.15fr) minmax(0, 1fr)' },
            alignItems: 'center',
            gap: { xs: 2, md: 4 },
          }}
        >
          <Box>
            <Paragraph>
              Work directly with your technical lead. Scaled Tech Consulting is a principal-led
              practice: you work with the person responsible for technical direction to understand
              the problem, weigh the options, and review progress as the work develops. When a
              project needs additional skills, we agree on roles, handoffs, and ownership.
            </Paragraph>
            <Paragraph>
              We focus on web applications, content platforms, integrations, and the administrative
              workflows that connect them. The work begins with a clear understanding of who uses
              the system, what they need to do, and where the current process falls short.
            </Paragraph>
            <Paragraph>
              Scaled Tech draws on experience from Expert Web Coder, a predecessor software practice
              established in 2004. That work includes shared CMS platforms for distinct sites,
              multilingual publishing, data-fed portals, and administrative tools for structured
              content. We bring those lessons to each engagement while scoping the work to its own
              goals and constraints.
            </Paragraph>
          </Box>
          <Box
            component='figure'
            sx={{
              m: 0,
              overflow: 'hidden',
              borderRadius: 3,
              border: '1px solid rgba(11, 37, 69, 0.12)',
              bgcolor: '#F8FAFC',
            }}
          >
            <img
              src={aboutDiscoveryLarge}
              srcSet={`${aboutDiscoverySmall} 768w, ${aboutDiscoveryLarge} 1536w`}
              sizes='(max-width: 900px) calc(100vw - 48px), 440px'
              width={1536}
              height={1024}
              loading='lazy'
              decoding='async'
              alt='Concept illustration of connected content, workflow, and application modules.'
              style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '3 / 2' }}
            />
            <Typography
              component='figcaption'
              variant='caption'
              sx={{ display: 'block', px: 2, py: 1.5, color: '#475569' }}
            >
              Discovery to delivery
            </Typography>
          </Box>
        </Box>
      </Container>

      <Container
        sx={{
          paddingTop: '20px',
        }}
      >
        <SectionTitle
          first='How we'
          second='work'
          type='titles'
        />
        <Paragraph>
          We map the workflow before choosing the tools. A useful solution may need a clear
          interface, an integration with an existing system, a better way to manage content, or all
          three. We explain the tradeoffs and build in stages that can be reviewed as the work
          progresses.
        </Paragraph>
        <Paragraph>
          The goal is software that people can use, understand, and maintain after launch.
        </Paragraph>
      </Container>

      <Container sx={{ paddingTop: '32px' }}>
        <Box
          component='section'
          aria-labelledby='delivery-practices'
        >
          <Typography
            id='delivery-practices'
            component='h2'
            variant='h4'
            sx={{ mb: 2, fontWeight: 700, color: '#0B2545' }}
          >
            Delivery practices
          </Typography>
          <Typography sx={{ mb: 3, color: '#475569' }}>
            These examples describe how we maintain this website. Project-specific requirements and
            support terms are agreed for each engagement.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <Box sx={{ p: 3, borderRadius: 3, bgcolor: '#F8FAFC' }}>
              <Typography
                component='h3'
                variant='h6'
                sx={{ mb: 1, fontWeight: 700 }}
              >
                Accessibility checks
              </Typography>
              <Typography color='text.secondary'>
                We plan for accessibility in design and delivery, with project-specific goals and
                testing agreed for each engagement.{' '}
                <MuiLink href='#accessibility-approach'>Explore our accessibility approach</MuiLink>
                .
              </Typography>
            </Box>
            <Box sx={{ p: 3, borderRadius: 3, bgcolor: '#F8FAFC' }}>
              <Typography
                component='h3'
                variant='h6'
                sx={{ mb: 1, fontWeight: 700 }}
              >
                Reviewed releases
              </Typography>
              <Typography color='text.secondary'>
                Website changes are tested and reviewed before release. We verify the running site
                after production deployment and keep deployment steps documented for handoff.
              </Typography>
            </Box>
            <Box sx={{ p: 3, borderRadius: 3, bgcolor: '#F8FAFC' }}>
              <Typography
                component='h3'
                variant='h6'
                sx={{ mb: 1, fontWeight: 700 }}
              >
                Maintenance and support
              </Typography>
              <Typography color='text.secondary'>
                For new work, we agree on who owns content updates, issue triage, operating notes,
                and ongoing support. Response times and service terms are set in the engagement,
                rather than assumed from this website.
              </Typography>
            </Box>
            <Box sx={{ p: 3, borderRadius: 3, bgcolor: '#F8FAFC' }}>
              <Typography
                component='h3'
                variant='h6'
                sx={{ mb: 1, fontWeight: 700 }}
              >
                Procurement contact
              </Typography>
              <Typography
                color='text.secondary'
                sx={{ overflowWrap: 'anywhere' }}
              >
                For a requested evaluation or procurement conversation, email{' '}
                <MuiLink href='mailto:info@scaledtech.consulting'>
                  info@scaledtech.consulting
                </MuiLink>
                . We can discuss the appropriate channel for project details and references.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
      <Container sx={{ pt: 5 }}>
        <Box
          component='section'
          aria-labelledby='accessibility-approach'
        >
          <Typography
            id='accessibility-approach'
            component='h2'
            variant='h4'
            sx={{ mb: 2, fontWeight: 700, color: '#0B2545' }}
          >
            Our accessibility approach
          </Typography>
          <Typography sx={{ mb: 2, color: '#475569' }}>
            We plan for accessibility as part of design and delivery: clear structure, meaningful
            labels, keyboard access, readable contrast, and layouts that adapt to different screens
            and text sizes.
          </Typography>
          <Typography sx={{ mb: 2, color: '#475569' }}>
            For this website, automated checks scan public pages and exercise keyboard focus and
            responsive layouts. VoiceOver checks have also been performed on selected interactions.
            These checks help find issues but do not establish WCAG conformance or ADA compliance.
          </Typography>
          <Typography sx={{ mb: 2, color: '#475569' }}>
            For a client project, we agree on applicable accessibility targets, such as WCAG 2.2 AA,
            and a testing plan for the actual content and workflows. Public-sector requirements can
            also cover documents and content supplied by others; the organization should determine
            its obligations with appropriate specialists.
          </Typography>
          <Typography sx={{ mb: 2, color: '#475569' }}>
            We discuss how authors will maintain accessible headings, links, image descriptions, and
            documents. When issues are found, the engagement can define how they are recorded,
            prioritized, corrected, retested, and maintained after launch.
          </Typography>
          <Typography sx={{ color: '#475569', overflowWrap: 'anywhere' }}>
            To discuss an accessibility plan for your project, email{' '}
            <MuiLink href='mailto:info@scaledtech.consulting'>info@scaledtech.consulting</MuiLink>.
          </Typography>
        </Box>
      </Container>
    </Container>
  );
}

export default AboutUs;
